import axios from 'axios';
import _ from 'lodash';
import { change, untouch, SubmissionError } from 'redux-form';
import { push } from 'connected-react-router';
import { NETWORK, FORM_ID, FLOW_TYPE } from '../../../../framework/App/AppConstants';
import setUrl from '../../../../framework/App/SetUrl';
import { PAGE_NAME, LEGACY_ENDPOINT, APPLICATION_STATUS, SUBMIT_API } from '../../../config/GBConstants';
import { setServerData } from '../../../../framework/store/ServerData/ServerDataActions';
import { spinnerClose } from '../../../../framework/components/FullPageSpinner/FullPageSpinnerActions';
import { processSubmissionErrors, handleSubmitFail } from '../../../../shared-components/FormElement/FormElementUtils';
import { scrollToTop } from '../../../../framework/App/SkeletonPage/SkeletonPageActions';
import { PERSONAL_INFO_SECTION, FINANCIAL_INFO_SECTION } from './ApplyPageFormConstants';
import { trackTransition, recordImpression } from '../../../../framework/store/FPTI/FPTIActions';
import { buildCapeTxnRunner } from '../../../../framework/App/CapeTransaction';
import {safeStringify} from '../../../../../../server/src/CAP/GPL/util/stringUtil';
import { expandSection } from '../../../components/Accordion/AccordionActions';

const runGbApplyCapeTxn = buildCapeTxnRunner('gb_apply');

export const handleInvalidSubmit = (errors) => dispatch => {
    // Required to expand page accordions containing errors
    if (_.get(errors, PERSONAL_INFO_SECTION, '')) {
        dispatch(expandSection(PERSONAL_INFO_SECTION));
    }
    if (_.get(errors, FINANCIAL_INFO_SECTION, '')) {
        dispatch(expandSection(FINANCIAL_INFO_SECTION));
    }
    handleSubmitFail(errors, dispatch);
};

export const handleRedirect = response => {
    if (_.get(response, 'data.redirect')) {
        setUrl(window, response.data.redirect);
        return true;
    }
    return false;
};

export const showSoftDecline = () => dispatch => {
    //for clearing out the DOB field on soft-decline
    dispatch(change(FORM_ID, `${PERSONAL_INFO_SECTION}.dobDay`, ''));
    dispatch(untouch(FORM_ID, `${PERSONAL_INFO_SECTION}.dobDay`));
    dispatch(change(FORM_ID, `${PERSONAL_INFO_SECTION}.dobMonth`, ''));
    dispatch(untouch(FORM_ID, `${PERSONAL_INFO_SECTION}.dobMonth`));
    dispatch(change(FORM_ID, `${PERSONAL_INFO_SECTION}.dobYear`, ''));
    dispatch(untouch(FORM_ID, `${PERSONAL_INFO_SECTION}.dobYear`));
    dispatch(scrollToTop());
};

export const submitApplicationApi = (data, localePrefix) => (dispatch, getState) => {
    return runGbApplyCapeTxn(() => {
        const state = getState();
        const { serverData = {} } = state;
        const { _csrf = '' } = serverData;

        // dob formatting for uk tesla/venice - for now
        const flowType = _.get(state, 'serverData.flow', '');
        if (flowType === FLOW_TYPE.TESLA || flowType === FLOW_TYPE.VENICE) {
            const [dobDay, dobMonth, dobYear] = _.get(data, `${PERSONAL_INFO_SECTION}.dob`).split('/');
            _.set(data, `${PERSONAL_INFO_SECTION}.dobDay`, dobDay);
            _.set(data, `${PERSONAL_INFO_SECTION}.dobMonth`, dobMonth);
            _.set(data, `${PERSONAL_INFO_SECTION}.dobYear`, dobYear);
        }

        return axios
            .request({
                url: SUBMIT_API,
                method: 'post',
                headers: {
                    ...NETWORK.REQUEST.XHR.HEADER,
                    'X-CSRF-Token': _csrf
                },
                data
            })
            .then(response => {
                // Catch server side validation
                if (!_.isEmpty(_.get(response, 'data.validation.errors'))) {
                    dispatch(scrollToTop());
                    dispatch(spinnerClose());
                    capeLog && capeLog(`submitApplicationApi:gb:response: server validation error: ${safeStringify(response.data.validation.errors)}`);
                    return Promise.reject(new SubmissionError(processSubmissionErrors(response.data.validation.errors.fieldError, localePrefix), state));
                } else {
                    if (handleRedirect(response)) {
                        return;
                    }

                    dispatch(spinnerClose());
                    dispatch(setServerData({
                        decisionModel: response.data,
                        creditLineInfo: _.get(response, 'data.creditLine'),
                        userLoggedIn: _.get(response, 'data.userLoggedIn'),
                        invalidAddressMsg: _.get(response, 'data.showInvalidAddress'),
                        showValidationSoftDecline: _.get(response, 'data.showValidationSoftDecline')
                    }));

                    const appStatus = _.get(response, 'data.applicationStatus');

                    if (appStatus === APPLICATION_STATUS.PENDING) {
                        dispatch(push(PAGE_NAME.CA_PAGE));
                    } else if (appStatus === APPLICATION_STATUS.APPROVED) { // for duplicate submit after Esign in case of DA
                        dispatch(push(PAGE_NAME.CA_PAGE));
                    } else if (appStatus === APPLICATION_STATUS.SOFT_DECLINE) {
                        dispatch(showSoftDecline());
                        recordImpression('applypage', appStatus);
                    } else if (appStatus === APPLICATION_STATUS.ANS_SOFT_DECLINE) {
                        const extra = {};
                        extra.ppc_status = appStatus;
                        dispatch(trackTransition('pp_credit_applypage', 'prepare_pp_credit_applypage', extra));
                        dispatch(showSoftDecline());
                        recordImpression('applypage', appStatus);
                    } else {
                        dispatch(push(PAGE_NAME.DECISION_PAGE));
                    }
                }
            })
            .catch(error => {
                if (error instanceof SubmissionError) {
                    return Promise.reject(error);
                }
                capeLog && capeLog('submitApplicationApi:gb:response: error: ' + (error && error.message));

                if (handleRedirect(error && error.response)) {
                    return;
                }

                if (_.get(error, 'response.data.err', false)) {
                    dispatch(setServerData({ errorModel: _.get(error, 'response.data', {}) }));
                    dispatch(push(PAGE_NAME.ERROR_PAGE));
                    dispatch(spinnerClose());
                } else {
                    // should never be here
                    capeLog && capeLog('response:error: unexpected error response for ApplyPage submit');
                    setUrl(window, LEGACY_ENDPOINT.PAGE_NAME.DECISION);
                }
            });
    });
};
